import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { User, SignOut, Shield, Bell, CreditCard, Lock, Envelope, WarningCircle, Trash, X } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { Switch } from '../components/ui/switch';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';
import GeoBlockSettingsCard from '../components/settings/GeoBlockSettingsCard';

const API = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { isSupported, isSubscribed, loading: pushLoading, subscribe, unsubscribe, testPush } = usePushNotifications();
  const [emailPrefs, setEmailPrefs] = useState({ email_critical_optin: true, email_announcements_optin: true });
  const [emailPrefsLoading, setEmailPrefsLoading] = useState(false);

  // Account deletion state
  const [deletionStatus, setDeletionStatus] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/users/email-preferences`)
      .then(r => setEmailPrefs(r.data))
      .catch(() => {});
    axios.get(`${API}/api/users/deletion-status`)
      .then(r => setDeletionStatus(r.data))
      .catch(() => {});
  }, []);

  const updatePref = async (key, value) => {
    const next = { ...emailPrefs, [key]: value };
    setEmailPrefs(next);
    setEmailPrefsLoading(true);
    try {
      await axios.put(`${API}/api/users/email-preferences`, next);
      toast.success('Email preferences updated');
    } catch {
      toast.error('Update failed');
      setEmailPrefs(emailPrefs);  // revert
    } finally { setEmailPrefsLoading(false); }
  };

  const requestDeletion = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Type DELETE in capitals to confirm');
      return;
    }
    if (!deletePassword) {
      toast.error('Password required');
      return;
    }
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/api/users/delete-account`, {
        password: deletePassword,
        reason: deleteReason,
      });
      toast.success(res.data.message, { duration: 8000 });
      setShowDeleteModal(false);
      // Log out so they don't auto-cancel by virtue of being logged in
      setTimeout(() => logout(), 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete account');
    } finally { setDeleting(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <h1 className="font-heading text-2xl font-bold text-white mb-6">Settings</h1>

      {/* Profile Section */}
      <div className="glass-light rounded-2xl p-6 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Account</h2>
        
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-slate-800 overflow-hidden">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user.display_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <User className="w-8 h-8 text-slate-500" />
              </div>
            )}
          </div>
          <div>
            <p className="text-white font-semibold text-lg">{user?.display_name}</p>
            <p className="text-slate-500">{user?.email}</p>
            <p className="text-sm text-blue-500 capitalize">{user?.role}</p>
          </div>
        </div>
        
        <Link
          to="/profile"
          className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-slate-400" />
            <span className="text-white">Edit Profile</span>
          </div>
          <span className="text-slate-500">&rsaquo;</span>
        </Link>
      </div>

      {/* Privacy Section */}
      <div className="glass-light rounded-2xl p-6 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Privacy & Safety</h2>
        
        <div className="space-y-1">
          <div className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-slate-400" />
              <div>
                <span className="text-white block">Age Verification</span>
                <span className="text-sm text-slate-500">
                  {user?.is_age_verified ? 'Verified' : 'Not verified'}
                </span>
              </div>
            </div>
            {user?.is_age_verified ? (
              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">Verified</span>
            ) : (
              <Link to="/verify-age" className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded-full">
                Verify Now
              </Link>
            )}
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-slate-400" />
              <span className="text-white">Private Profile</span>
            </div>
            <Switch />
          </div>
        </div>
      </div>

      {/* Notifications Section */}
      <div className="glass-light rounded-2xl p-6 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Notifications</h2>
        
        <div className="space-y-1">
          <div className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-slate-400" />
              <div>
                <span className="text-white block">Push Notifications</span>
                {!isSupported && <span className="text-xs text-slate-600">Not supported in this browser</span>}
                {isSupported && isSubscribed && <span className="text-xs text-green-400">Active</span>}
              </div>
            </div>
            <Switch
              checked={isSubscribed}
              disabled={!isSupported || pushLoading}
              onCheckedChange={async (checked) => {
                if (checked) {
                  const ok = await subscribe();
                  if (ok) toast.success('Push notifications enabled!');
                  else toast.error('Could not enable notifications');
                } else {
                  await unsubscribe();
                  toast.success('Push notifications disabled');
                }
              }}
              data-testid="push-notifications-toggle"
            />
          </div>

          {isSubscribed && (
            <button
              onClick={async () => {
                const ok = await testPush();
                if (ok) toast.success('Test notification sent!');
                else toast.error('Failed to send test');
              }}
              className="ml-12 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              data-testid="test-push-btn"
            >
              Send test notification
            </button>
          )}
        </div>
      </div>

      {/* Email Preferences */}
      <div className="glass-light rounded-2xl p-6 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Email Preferences</h2>
        <div className="space-y-1">
          <div className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-3">
              <WarningCircle className="w-5 h-5 text-amber-400" />
              <div>
                <span className="text-white block">Critical alerts</span>
                <span className="text-xs text-slate-500">Security, billing, payment, and account warnings</span>
              </div>
            </div>
            <Switch
              checked={emailPrefs.email_critical_optin}
              disabled={emailPrefsLoading}
              onCheckedChange={(checked) => updatePref('email_critical_optin', checked)}
              data-testid="email-critical-toggle"
            />
          </div>
          <div className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-3">
              <Envelope className="w-5 h-5 text-blue-400" />
              <div>
                <span className="text-white block">Announcements & news</span>
                <span className="text-xs text-slate-500">New features, platform updates, promotions</span>
              </div>
            </div>
            <Switch
              checked={emailPrefs.email_announcements_optin}
              disabled={emailPrefsLoading}
              onCheckedChange={(checked) => updatePref('email_announcements_optin', checked)}
              data-testid="email-announcements-toggle"
            />
          </div>
        </div>
      </div>

      {/* Geo-blocking — creators/admins only */}
      {(user?.role === 'creator' || user?.role === 'admin') && (
        <div className="mb-6">
          <GeoBlockSettingsCard />
        </div>
      )}

      {/* Subscription & Billing */}
      <div className="glass-light rounded-2xl p-6 mb-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Subscription & Billing</h2>
        
        <Link
          to="/tokens"
          className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-slate-400" />
            <span className="text-white">Manage Tokens</span>
          </div>
          <span className="text-slate-500">&rsaquo;</span>
        </Link>
      </div>

      {/* Danger Zone */}
      <div className="glass-light rounded-2xl p-6">
        <h2 className="text-sm font-bold uppercase tracking-wider text-blue-500 mb-4">Danger Zone</h2>

        {deletionStatus?.deletion_pending && (
          <div className="mb-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-200 text-sm">
            <p className="font-bold mb-1">Account deletion scheduled.</p>
            <p>Your account will be permanently anonymized on{' '}
              <span className="font-mono text-white">
                {new Date(deletionStatus.deletion_scheduled_at).toLocaleDateString()}
              </span>.
              You can cancel by simply logging back in any time before then — or do nothing more, and we&apos;ll handle it.
            </p>
          </div>
        )}

        <button
          onClick={logout}
          className="flex items-center gap-3 p-4 w-full rounded-xl text-blue-500 hover:bg-blue-500/10 transition-colors"
          data-testid="settings-logout-btn"
        >
          <SignOut className="w-5 h-5" />
          <span>Log Out</span>
        </button>

        {!deletionStatus?.deletion_pending && (
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-3 p-4 w-full rounded-xl text-red-500 hover:bg-red-500/10 transition-colors"
            data-testid="settings-delete-account-btn"
          >
            <Trash className="w-5 h-5" />
            <span>Delete My Account</span>
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-8">
        <p className="text-slate-600 text-sm">LustLink · Ganso Gordo Producciones LLC</p>
        <div className="flex items-center justify-center gap-4 mt-2 text-xs text-slate-700 flex-wrap">
          <Link to="/legal/privacy" className="hover:text-slate-500">Privacy Policy</Link>
          <Link to="/legal/terms" className="hover:text-slate-500">Terms of Service</Link>
          <Link to="/legal/refund" className="hover:text-slate-500">Refund Policy</Link>
          <Link to="/legal" className="hover:text-slate-500">All Legal Docs</Link>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="delete-account-modal">
          <div className="bg-slate-900 border border-red-500/30 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto overscroll-contain">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Trash weight="fill" className="w-5 h-5 text-red-500" />
                <h3 className="font-heading text-xl font-bold text-white">Delete Your Account</h3>
              </div>
              <button onClick={() => setShowDeleteModal(false)} className="text-slate-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-200 text-xs space-y-1">
              <p className="font-bold">What happens next:</p>
              <ul className="list-disc list-inside space-y-0.5 ml-1">
                <li>Your profile + content disappear from the site immediately.</li>
                <li>You have <strong>30 days</strong> to change your mind — just log back in.</li>
                <li>After 30 days, your account is permanently anonymized.</li>
                <li>Records we are legally required to keep (KYC, payments, tax) are retained per our <Link to="/legal/privacy" className="text-blue-400 underline">Privacy Policy</Link>.</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Confirm with your password</label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
                  data-testid="delete-password-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Reason (optional, helps us improve)</label>
                <textarea
                  value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)}
                  rows={2}
                  maxLength={500}
                  className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  Type <span className="text-red-400 font-mono">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={e => setDeleteConfirmText(e.target.value)}
                  className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white font-mono"
                  data-testid="delete-confirm-input"
                />
              </div>
              <button
                onClick={requestDeletion}
                disabled={deleting || deleteConfirmText !== 'DELETE' || !deletePassword}
                className="w-full py-3 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="delete-confirm-btn">
                {deleting ? 'Scheduling deletion…' : 'Permanently delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
